/// <amd-module name="VSS/ExtUIServices/HostPageLayoutService" />
import * as React from "react";

import { ExternalContent } from "VSS/Ext/ExternalContent";
import { ServiceOptions, Services, setRemoteSerializationSettings, VssService } from "VSS/Platform/Context";
import { IVssLayoutManager } from "VSS/Platform/Layout";
import { ContentSize } from "VSSUI/Callout";
import { Dialog } from "VSSUI/Dialog";
import { Panel } from "VSSUI/Panel";

import * as Resources from "VSS/ExtUIServices/Resources";
import { IButtonProps } from "VSSUI/Button";

/**
 * Options for showing host dialogs
 */
export interface IDialogOptions<TResult> {
    /**
     * Dialog title
     */
    title?: string;

    /**
     * Callback invoked when the dialog is closed
     */
    onClose?: (result: TResult | undefined) => void;

    /**
     * Optional initial configuration for the dialog content
     */
    configuration?: any;

    /**
     * If true, clicking outside the dialog closes it
     * @default true
     */
    lightDismiss?: boolean;
}

/**
 * Options for showing a message dialog
 */
export interface IMessageDialogOptions extends IDialogOptions<boolean> {
    /**
     * If true, show the cancel button
     */
    showCancel?: boolean;

    /**
     * Custom text for the OK button
     */
    okText?: string;

    /**
     * Custom text for the Cancel button
     */
    cancelText?: string;
}

/**
 * Options for showing panels
 */
export interface IPanelOptions<TResult> {
    /**
     * Callback invoked when the dialog is closed
     */
    onClose?: (result: TResult | undefined) => void;

    /**
     * The panel title you want to display.
     */
    title?: string;

    /**
     * Optional, description of panel.
     */
    description?: string;

    /**
     * Size of the panel.
     * @default ContentSize.Medium
     */
    size?: ContentSize;

    /**
     * Optional initial configuration for the panel content
     */
    configuration?: any;
}

/**
 * Service for external content to show a panel in the host frame
 */
export interface IHostPageLayoutService {
    /**
     * Open a dialog in the host frame, showing custom external content
     *
     * @param contentContributionId Id of the dialog content contribution that specifies the content to display in the dialog.
     * @param options Dialog options
     */
    openCustomDialog: <TResult>(contentContributionId: string, options?: IDialogOptions<TResult>) => void;

    /**
     * Open a dialog in the host frame, showing the specified text message, an OK and optional Cancel button
     *
     * @param message Dialog message text
     * @param options Dialog options
     */
    openMessageDialog: (message: string, options?: IMessageDialogOptions) => void;

    /**
     * Open a panel in the host frame, showing custom external content
     *
     * @param contentContributionId Id of the panel content contribution that specifies the content to display in the panel.
     * @param options Panel display options
     */
    openPanel: <TResult>(contentContributionId: string, options: IPanelOptions<TResult>) => void;
    /**
     * Gets whether the page is currently in full screen mode
     */
    getFullScreenMode(): boolean;

    /**
     * Enter or exit full screen mode
     *
     * @param fullScreenMode True to enter full-screen mode, false to exit.
     */
    setFullScreenMode(fullScreenMode: boolean): void;
}

class HostPageLayoutService extends VssService implements IHostPageLayoutService {
    public getFullScreenMode(): boolean {
        const layoutManager = this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        return layoutManager.getFullScreenMode().value;
    }

    public openCustomDialog<TResult>(contentContributionId: string, options?: IDialogOptions<TResult>): void {
        const dialogOptions = options || {};

        const layoutManager = this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(onDismiss => {
            const dialog = {
                close: (result: TResult | undefined) => {
                    if (dialogOptions.onClose) {
                        dialogOptions.onClose(result);
                    }
                    onDismiss();
                }
            };
            setRemoteSerializationSettings(dialog, { proxyFunctions: true });

            return (
                <Dialog
                    onDismiss={() => {
                        dialog.close(undefined);
                    }}
                    lightDismiss={dialogOptions.lightDismiss}
                    titleProps={{ text: dialogOptions.title }}
                >
                    <ExternalContent
                        contributionId={contentContributionId}
                        initialHandshakeData={{
                            ...dialogOptions.configuration,
                            dialog
                        }}
                    />
                </Dialog>
            );
        });
    }

    public openMessageDialog(message: string, options?: IMessageDialogOptions): void {
        const dialogOptions = options || {};

        const layoutManager = this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(onDismiss => {
            const closeDialog = (result: boolean) => {
                if (dialogOptions.onClose) {
                    dialogOptions.onClose(result);
                }
                onDismiss();
            };

            const buttons: IButtonProps[] = [
                {
                    text: dialogOptions.okText || Resources.ok,
                    primary: true,
                    onClick: () => {
                        closeDialog(true);
                    }
                }
            ];

            if (dialogOptions.showCancel !== false) {
                buttons.push({
                    text: dialogOptions.cancelText || Resources.cancel,
                    onClick: () => {
                        closeDialog(false);
                    }
                });
            }

            return (
                <Dialog
                    onDismiss={() => {
                        closeDialog(false);
                    }}
                    footerButtonProps={buttons}
                    lightDismiss={dialogOptions.lightDismiss}
                    titleProps={{ text: dialogOptions.title }}
                >
                    <span>{message}</span>
                </Dialog>
            );
        });
    }

    public openPanel<TResult>(contentContributionId: string, options: IPanelOptions<TResult>): void {
        const panelOptions = options || {};

        const layoutManager = this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(onDismiss => {
            const panel = {
                close: (result: TResult | undefined) => {
                    if (panelOptions.onClose) {
                        panelOptions.onClose(result);
                    }
                    onDismiss();
                }
            };
            setRemoteSerializationSettings(panel, { proxyFunctions: true });

            return (
                <Panel
                    size={panelOptions.size}
                    titleProps={{ text: panelOptions.title }}
                    description={panelOptions.description}
                    onDismiss={() => {
                        panel.close(undefined);
                    }}
                >
                    <ExternalContent
                        className="flex-grow"
                        contributionId={contentContributionId}
                        initialHandshakeData={{
                            ...panelOptions.configuration,
                            panel
                        }}
                    />
                </Panel>
            );
        });
    }

    public setFullScreenMode(fullScreenMode: boolean): void {
        const layoutManager = this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        return layoutManager.setFullScreenMode(fullScreenMode);
    }
}

Services.add("ms.vss-features.host-page-layout-service", { serviceFactory: HostPageLayoutService, options: ServiceOptions.Remotable });

// Registering these for compat.
Services.add("ms.vss-features.host-dialog-service", { serviceFactory: HostPageLayoutService, options: ServiceOptions.Remotable });
Services.add("ms.vss-features.ms.vss-features.host-panel-service", { serviceFactory: HostPageLayoutService, options: ServiceOptions.Remotable });
